import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import {
  faCirclePlus,
  faEllipsisVertical,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import { Dropdown, Menu, MenuButton, MenuItem } from "@mui/joy";
import { Avatar, Breadcrumbs, Pagination, Stack } from "@mui/material";
import axios from "axios";
import TransparentButton from "common/components/Button/TransparentButton";
import CustomBreadcrumb from "common/components/CustomBreadcrumb";
import LoadingSpinner from "common/components/LoadingSpinner";
import ItemNotFoundContainer from "common/containers/ItemNotFoundContainer";
import { useAuth } from "common/hooks/useAuth";
import { BACKEND_URL_CONFIG } from "config";
import { ERRORS } from "errors";
import MainLayout from "layouts/MainLayout";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./LemmasManagementPage.module.css";

export default function LemmasManagementPage() {
  // States
  const [loading, setLoading] = useState(false);
  const [allLemmas, setAllLemmas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Contexts
  const navigate = useNavigate();
  const { currentUser, isUserAdmin, isUserManager } = useAuth();

  // Constants
  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(allLemmas?.length / itemsPerPage);
  const filteredLemmas = useMemo(
    () => allLemmas.slice(indexOfFirstItem, indexOfLastItem),
    [allLemmas, indexOfFirstItem, indexOfLastItem]
  );

  useEffect(() => {
    fetchLemmas();
  }, []);

  const fetchLemmas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(BACKEND_URL_CONFIG.getLemmasFilesList, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response.status === 200) {
        setAllLemmas(response.data);
      }
    } catch (error) {
      if (error.response.status === 403) {
        toast.error(ERRORS.error_129);
        return;
      }
      toast.error(ERRORS.error_152);
    } finally {
      setLoading(false);
    }
  };

  const deleteLemmaFile = async (lemmasFileId) => {
    setLoading(true);
    const deleteLemmaFileEndpoint = BACKEND_URL_CONFIG.deleteLemmasFile.replace(
      "{lemmasFileId}",
      lemmasFileId
    );
    try {
      const response = await axios.delete(deleteLemmaFileEndpoint, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response.status === 200) {
        fetchLemmas();
        toast.success("Le fichier a été supprimé");
        return;
      }
    } catch (error) {
      if (error.response.status === 403) {
        toast.error(ERRORS.error_129);
        return;
      }
      toast.error(ERRORS.error_153);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <MainLayout>
        <div className={styles.container}>
          <div className={styles["breadcrumbs-wrapper"]}>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <CustomBreadcrumb
                component="a"
                href="/"
                label="Page d'accueil"
                icon={<FontAwesomeIcon icon={faHome} />}
              />
              <CustomBreadcrumb component="a" href="#" label="Lemmes" />
            </Breadcrumbs>
          </div>
          <div className={styles["title-wrapper"]}>
            <div className={styles.title}>
              <span>Lemmes existants</span>
              {loading && <LoadingSpinner />}
            </div>
            <div className={styles["actions-buttons-wrapper"]}>
              <TransparentButton onClick={() => navigate("/lemmas/add")}>
                <FontAwesomeIcon icon={faCirclePlus} />
                Nouveau
              </TransparentButton>
            </div>
          </div>
          <div className={styles["main-section"]}>
            {allLemmas.length === 0 ? (
              <ItemNotFoundContainer message="Aucun fichier de lemmes n’a été trouvé. Vous pouvez en ajouter un !" />
            ) : (
              <div className={styles["table-section-wrapper"]}>
                <table id={styles["files-table"]}>
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Créé le</th>
                      <th>Créé par</th>
                      <th>Certifié</th>
                      <th>Versions</th>
                      <th>Projets</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLemmas?.map((lemmaFile) => (
                      <tr className={styles.row} key={lemmaFile.id}>
                        <td
                          style={{
                            fontWeight: "bold",
                            textDecoration: "underline",
                          }}
                        >
                          {lemmaFile.name}
                        </td>
                        <td>
                          {new Date(lemmaFile.created_at * 1000).toLocaleString(
                            "fr-FR",
                            {
                              timeZone: "Europe/Paris",
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            }
                          )}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                            }}
                          >
                            {lemmaFile.created_by_details.length > 0 &&
                              lemmaFile.created_by_details.map((user, index) =>
                                user.photo_url ? (
                                  <Avatar
                                    key={index}
                                    alt={user.initiales}
                                    src={user.photo_url}
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      fontSize: 13,
                                      color: "#000",
                                      backgroundColor: "#cecece",
                                    }}
                                  />
                                ) : (
                                  <Avatar
                                    key={index}
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      fontSize: 13,
                                      color: "#000",
                                      backgroundColor: "#cecece",
                                    }}
                                  >
                                    {user.initiales}
                                  </Avatar>
                                )
                              )}
                          </div>
                        </td>
                        <td>
                          {lemmaFile.is_certified ? (
                            <FontAwesomeIcon
                              icon={faCircleCheck}
                              style={{ color: "#2b652b", fontSize: "20px" }}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              justifyContent: "center",
                              overflow: "overlay",
                              maxWidth: "250px",
                              maxHeight: "80px",
                            }}
                          >
                            {lemmaFile.used_in_versions_information?.length >
                            0 ? (
                              lemmaFile.used_in_versions_information?.map(
                                (version) => (
                                  <span
                                    title={version.name}
                                    key={version.id}
                                    className={styles["tag-wrapper"]}
                                    style={{
                                      background: "#aa5f8c",
                                    }}
                                    onClick={() =>
                                      navigate(
                                        `/projects/${version.project_id}/version/${version.id}/run/load`
                                      )
                                    }
                                  >
                                    {version.name.substring(0, 10)}...
                                  </span>
                                )
                              )
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              justifyContent: "center",
                              overflow: "overlay",
                              maxWidth: "250px",
                            }}
                          >
                            {lemmaFile.used_in_projects_information?.length >
                            0 ? (
                              lemmaFile.used_in_projects_information?.map(
                                (project) => (
                                  <span
                                    title={project.name}
                                    key={project.id}
                                    className={styles["tag-wrapper"]}
                                    style={{
                                      background: "#718bc5",
                                    }}
                                    onClick={() =>
                                      navigate(`/projects/${project.id}`)
                                    }
                                  >
                                    {project.name.substring(0, 10)}...
                                  </span>
                                )
                              )
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <Dropdown>
                            <MenuButton
                              style={{ padding: "11px 16px", border: "unset" }}
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faEllipsisVertical} />
                            </MenuButton>
                            <Menu>
                              <MenuItem
                                disabled={!isUserAdmin && !isUserManager}
                                onClick={() =>
                                  navigate(`/lemmas/edit/${lemmaFile.id}`)
                                }
                              >
                                Modifier
                              </MenuItem>
                              <MenuItem
                                disabled={!isUserAdmin && !isUserManager}
                                onClick={() => deleteLemmaFile(lemmaFile.id)}
                              >
                                Supprimer
                              </MenuItem>
                            </Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {filteredLemmas.length > 0 && (
              <div className={styles["pagination-container"]}>
                <Stack spacing={2}>
                  <Pagination
                    page={currentPage}
                    onChange={(e, value) => setCurrentPage(value)}
                    count={totalPages}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>
      </MainLayout>
    </Fragment>
  );
}
